import { useEffect, useMemo } from 'react';
import {
  UrlParamName,
  useAppUrlParams,
} from 'hooks/navigation/useAppUrlParams';
import {
  documentsToDocumentsOnInspectionView,
} from 'presentation/document/documentsToDocumentsOnInspectionView';
import { useProcessesObject } from 'store/process';
import { useProjectOrganizationId } from 'store/project';
import { useDocuments } from 'store/documents';
import { useSites } from 'store/site';
import { useLevels } from 'store/level';
import { useCompanies } from 'store/company';
import { useContracts } from 'store/contract';
import { useInspectionStore } from '../../store';
import { useInspectionTemplate } from '../useInspectionTemplate';
import { useSynchronizeInspectionIdFromUrl } from '../useSynchronizeInspectionIdFromUrl';
import { useTransformInspectionModel } from '../useTransformInspectionModel';
import { useInspectionTemplateInitializer } from './useInspectionTemplateInitializer';
import { useInspectionIssuesLoader } from 'store/issue';
import { documentsToDocumentsOnTemplateView } from 'presentation/document/documentsToDocumentsOnTemplateView';
import { prefetchDocumentThumbnails } from 'components/common/graphicUploader/documentThumbnailResolver';
import { useWorktypes } from 'store/fieldValue';

export function useInspectionInitializer(): void {
  const inspectionIdParam = useAppUrlParams(UrlParamName.INSPECTION_ID);

  // External data sources
  const { inspectionTemplate } = useInspectionTemplate();
  const { items: sites } = useSites();
  const { items: levels } = useLevels();
  const { items: workTypes } = useWorktypes();
  const { items: companies } = useCompanies();
  const { items: contracts } = useContracts();
  const processes = useProcessesObject();
  const organizationId = useProjectOrganizationId();

  // Store selectors
  const setInspection = useInspectionStore.use.setInspection();
  const setInspectionDocuments = useInspectionStore.use.setInspectionDocuments();
  const setInspectionDocumentsLoading =
    useInspectionStore.use.setInspectionDocumentsLoading();
  const setInspectionTemplateDocuments = useInspectionStore.use.setInspectionTemplateDocuments();
  const setIgnoreDocumentsStore = useInspectionStore.use.setIgnoreDocumentsStore();
  const inspectionModel = useInspectionStore.use.inspectionModel();
  const inspectionModelId = inspectionModel?._id;
  const ignoreDocumentsStore = useInspectionStore.use.ignoreDocumentsStore();
  const openInspection = useInspectionStore.use.openInspection();
  const subscribeToInspectionChanges = useInspectionStore.use.subscribeToInspectionChanges();

  useSynchronizeInspectionIdFromUrl(inspectionIdParam);

  useEffect(() => {
    if (inspectionIdParam) {
      void openInspection(inspectionIdParam);
    }
  }, [inspectionIdParam, openInspection]);

  useEffect(subscribeToInspectionChanges, [subscribeToInspectionChanges]);

  // Initialize template when inspection is loaded
  useInspectionTemplateInitializer(inspectionModel?.template, {
    skipDocuments: true,
  });
  useInspectionIssuesLoader(inspectionModelId);

  // Documents query based on inspection
  const documentQuery = useMemo(() => {
    if (ignoreDocumentsStore || !inspectionModel) {
      return undefined;
    }
    return {
      inspectionId: inspectionModel._id,
      templateId: inspectionModel.template,
    };
  }, [
    inspectionModel?._id,
    inspectionModel?.template,
    ignoreDocumentsStore,
  ]);

  const { documents, loading: documentsLoading } = useDocuments(documentQuery);

  useEffect(() => {
    if (!inspectionModel) {
      return;
    }

    setIgnoreDocumentsStore(false);

    if (!documentQuery) {
      return;
    }

    setInspectionDocumentsLoading(documentsLoading);

    if (documentsLoading) {
      return;
    }

    const inspectionDocs = documentsToDocumentsOnInspectionView(documents);
    setInspectionDocuments(inspectionDocs);

    const templateDocs = documentsToDocumentsOnTemplateView(documents);
    setInspectionTemplateDocuments(templateDocs);

    prefetchDocumentThumbnails([
      ...templateDocs.templateDocuments,
      ...Object.values(templateDocs.sectionDocuments).flat(),
      ...Object.values(templateDocs.checklistDocuments).flat(),
      ...Object.values(inspectionDocs).flat(),
    ]);
  }, [
    documentQuery,
    documents,
    documentsLoading,
    inspectionModel,
    setIgnoreDocumentsStore,
    setInspectionDocuments,
    setInspectionDocumentsLoading,
    setInspectionTemplateDocuments,
  ]);

  const inspectionOnView = useTransformInspectionModel(
    inspectionModel,
    inspectionTemplate,
    processes,
    sites,
    levels,
    workTypes,
    companies,
    contracts,
    organizationId || null
  );

  // Publishes the inspection built from several stores, so its readers do not each build it again.
  useEffect(() => {
    setInspection(inspectionOnView);
  }, [inspectionOnView, setInspection]);
}
